<div class="space-y-6">
  <div>
    <h2 class="mb-2 text-xl font-semibold text-gray-900">
      {{ 'contribute.chooseProject.title' | transloco }}
    </h2>
    <p class="text-gray-600">
      {{ 'contribute.chooseProject.description' | transloco }}
    </p>
  </div>

  <!-- Filters -->
  <div class="flex flex-wrap items-center gap-4">
    <div class="w-48">
      <p-select
        [options]="projectTypeOptions"
        [(ngModel)]="selectedType"
        (onChange)="onFilterChange()"
        placeholder="{{ 'contribute.filters.projectType' | transloco }}"
        [showClear]="true"
        styleClass="w-full"
      />
    </div>
    <div class="w-48">
      <p-select
        [options]="regionOptions"
        [(ngModel)]="selectedRegion"
        (onChange)="onFilterChange()"
        placeholder="{{ 'contribute.filters.region' | transloco }}"
        [showClear]="true"
        styleClass="w-full"
      />
    </div>
    <div class="w-48">
      <p-select
        [options]="certificationOptions"
        [(ngModel)]="selectedCertification"
        (onChange)="onFilterChange()"
        placeholder="{{ 'contribute.filters.certification' | transloco }}"
        [showClear]="true"
        styleClass="w-full"
      />
    </div>

    <!-- View all projects button - shown only when filters are active -->
    @if (hasActiveFilters) {
      <button
        pButton
        size="small"
        severity="secondary"
        [text]="true"
        icon="pi pi-list"
        (click)="clearFilters()"
      >
        {{ 'contribute.filters.viewAll' | transloco }}
      </button>
    }
  </div>

  <!-- Projects Grid -->
  @if (loading) {
    <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      @for (_ of [1, 2, 3, 4, 5, 6]; track $index) {
        <div class="rounded-lg border p-4">
          <p-skeleton width="100%" height="150px" />
          <p-skeleton width="70%" height="1.5rem" styleClass="mt-4" />
          <p-skeleton width="100%" height="3rem" styleClass="mt-2" />
          <p-skeleton width="40%" height="1rem" styleClass="mt-2" />
        </div>
      }
    </div>
  } @else if (filteredProjects().length === 0) {
    <div class="rounded-lg bg-gray-50 p-8 text-center">
      <p class="text-gray-600">{{ 'contribute.chooseProject.noProjects' | transloco }}</p>
    </div>
  } @else {
    <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      @for (project of filteredProjects(); track project.id) {
        <div
          class="cursor-pointer rounded-lg border-2 p-4 transition-all hover:shadow-md"
          [class.border-primary-500]="isSelected(project)"
          [class.bg-primary-50]="isSelected(project)"
          [class.border-gray-200]="!isSelected(project)"
          (click)="openDetailModal(project, $event)"
          (keydown.enter)="openDetailModal(project, $event)"
          (keydown.space)="openDetailModal(project, $event); $event.preventDefault()"
          tabindex="0"
          role="button"
          [attr.aria-label]="'View details for ' + getProjectName(project)"
        >
          @if (project.mainImageUrl) {
            <img
              [src]="project.mainImageUrl"
              [alt]="getProjectName(project)"
              class="mb-3 h-36 w-full rounded-lg object-cover"
            />
          } @else {
            <div class="mb-3 flex h-36 w-full items-center justify-center rounded-lg bg-gradient-to-br from-primary-100 to-primary-50">
              <img
                src="/assets/Logo___Greentripper_icon_primary.svg"
                alt="Greentripper"
                class="h-12 opacity-40"
              />
            </div>
          }

          <h3 class="mb-1 font-semibold text-gray-900">{{ getProjectName(project) }}</h3>
          <p class="mb-2 line-clamp-2 text-sm text-gray-600">
            {{ getProjectDescription(project) }}
          </p>

          <div class="mb-2 flex flex-wrap gap-1">
            @if (project.isCertifiedOffset) {
              <span class="inline-flex items-center rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-700">
                {{ 'contribute.tags.certified' | transloco }}
              </span>
            }
            <span class="inline-flex items-center rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-700">
              {{ getProjectTypeLabel(project.projectType) }}
            </span>
          </div>

          <div class="flex items-center justify-between">
            <span class="text-lg font-bold text-primary-600">
              {{ project.pricePerTonne | number:'1.2-2' }} EUR/t
            </span>
          </div>

          <!-- Select button -->
          <div class="mt-3">
            <button
              pButton
              size="small"
              [severity]="isSelected(project) ? 'success' : 'primary'"
              [outlined]="!isSelected(project)"
              [icon]="isSelected(project) ? 'pi pi-check' : ''"
              (click)="selectProject(project); $event.stopPropagation()"
              class="w-full"
              [class.btn-selected]="isSelected(project)"
            >
              {{ isSelected(project) ? ('contribute.chooseProject.selected' | transloco) : ('contribute.chooseProject.select' | transloco) }}
            </button>
          </div>
        </div>
      }
    </div>
  }

  <!-- Project Detail Modal -->
  <app-project-detail-modal
    [project]="detailProject()"
    [visible]="showDetailModal()"
    (visibleChange)="showDetailModal.set($event)"
    (selectProject)="onModalSelectProject($event)"
  />

  <!-- Navigation -->
  <div class="flex justify-end pt-4">
    <button
      pButton
      icon="pi pi-arrow-right"
      iconPos="right"
      [disabled]="!wizardService.canProceedStep1()"
      (click)="onNext()"
    >
      {{ 'common.next' | transloco }}
    </button>
  </div>
</div>
